import {getTestProps} from '@benchling/js-test-utils';
import {Button, createStyles} from '@mantine/core';
import React, {useMemo, useState} from 'react';

import {NdcSpreadsheetImportValidationError} from 'antibodies/nested-data-creation/spreadsheet-import/constants/AntibodyNestedDataCreationSpreadsheetImportConstants';
import {assert} from 'Asserts';
import {ERRORS_TABLE_PAGINATION_LIMIT} from 'bulk-import/SpreadsheetBulkImportConstants';
import ResultsPager from 'ui-library/ResultsPager';
import copyToClipboard from 'util/clipboard/copyToClipboard';
import {formatRows} from 'util/ExportUtil';

// We add 2 to the displayed rowIndex because errors are returned 0-indexed and the backend disregards
// the header row
const ROW_INDEX_DISPLAY_OFFSET = 2;

const useStyles = createStyles(() => ({
  copyErrorsContainer: {
    marginLeft: 'auto',
  },
  rowNumberCell: {
    whiteSpace: 'nowrap',
    width: '1%',
    // Double class selector with && (e.g. `.mantine-abc123.mantine-abc123`) to beat bootstrap specificity
    '&&': {
      verticalAlign: 'middle',
    },
  },
  messageCell: {
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  },
}));

export function getTSVErrors(validationErrors: Array<NdcSpreadsheetImportValidationError>): string {
  const rows = [['Row #', 'Message']];
  for (const error of validationErrors) {
    rows.push([`${error.rowIndex + ROW_INDEX_DISPLAY_OFFSET}`, error.errorMessage]);
  }
  return formatRows(rows, '\t');
}

export interface ValidationErrorsPaneProps {
  validationErrors: Array<NdcSpreadsheetImportValidationError>;
}

export default function ValidationErrorsPane({validationErrors}: ValidationErrorsPaneProps): JSX.Element {
  assert(validationErrors.length > 0, 'ValidationErrorsPane requires at least one validation error');

  const {classes} = useStyles();
  const [pageOffset, setOffset] = useState<number>(0);
  const pageErrors = validationErrors.slice(pageOffset, pageOffset + ERRORS_TABLE_PAGINATION_LIMIT);
  const tsvErrors = useMemo(() => getTSVErrors(validationErrors), [validationErrors]);

  return (
    <div>
      <table
        className="table"
        {...getTestProps({component: 'ValidationErrorsPane', element: 'validationErrorsTable'})}
      >
        <thead>
          <tr>
            <th className={classes.rowNumberCell}>Row #</th>
            <th>
              <div className="u-vertical-center-items">
                Message
                <div className={classes.copyErrorsContainer}>
                  <Button
                    variant="ghost"
                    compact={true}
                    onClick={() =>
                      copyToClipboard(tsvErrors, {
                        showToastOnSuccess: true,
                        toastText: 'Copied errors to clipboard.',
                      })
                    }
                  >
                    Copy errors
                  </Button>
                </div>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {pageErrors.map((error, index) => (
            <tr key={pageOffset + index}>
              <td className={`bulk-import--column-text-column ${classes.rowNumberCell}`}>
                <span className="bulk-import--table-text">{error.rowIndex + ROW_INDEX_DISPLAY_OFFSET}</span>
              </td>
              <td className={`bulk-import--column-text-column ${classes.messageCell}`}>
                <span className="bulk-import--table-text">{error.errorMessage}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {validationErrors.length > ERRORS_TABLE_PAGINATION_LIMIT && (
        <ResultsPager
          setOffset={setOffset}
          pagination={{
            offset: pageOffset,
            limit: ERRORS_TABLE_PAGINATION_LIMIT,
            total: validationErrors.length,
          }}
          isLoading={false}
        />
      )}
    </div>
  );
}
